import type { BaseMarkerType, MarkerIconSize, MarkerImageType, MarkerInfo, MarkerStyleConfig } from '@/types/map/marker/marker-types.ts';
import { markerIcons } from '@/config/markers-config.ts';

/**
 * Marker Service
 * Handles marker creation and styling
 */

// =================================================================================================
// CONSTANTS & CONFIGURATIONS
// =================================================================================================

/**
 * Size class mapping for image markers
 */
const SIZE_CLASS_MAP: Record<MarkerIconSize, string> = {
   sm: 'size-4 md:size-5',
   md: 'size-7 md:size-9',
   lg: 'size-12 md:size-16',
};

/**
 * Marker style configurations for selected/unselected states
 */
export const MARKER_STYLE_CONFIGS: Partial<Record<BaseMarkerType, MarkerStyleConfig>> = {
   // HTML-based marker (div element)
   permit: {
      unselected: {
         classes: ['bg-black', 'text-white', 'border-white'],
         removeClasses: ['bg-[#F4FF7F]', 'text-black', 'border-black', 'scale-110', 'drop-shadow-lg', 'font-medium'],
      },
      selected: {
         classes: ['bg-[#F4FF7F]', 'text-black', 'border-black', 'scale-110', 'drop-shadow-lg', 'font-medium'],
         removeClasses: ['bg-black', 'text-white', 'border-white'],
      },
   },
   // Image-based marker (img element)
   DEFAULT: {
      unselected: {
         classes: [],
         removeClasses: ['size-8', 'md:size-9', 'drop-shadow-lg'],
      },
      selected: {
         classes: ['size-8', 'md:size-9', 'drop-shadow-lg'],
         removeClasses: [],
      },
   },
};

// =================================================================================================
// MARKER CREATION
// =================================================================================================

/**
 * Creates an image-based marker (for most marker types)
 */
export function createSourceMarker(
   markerObj: MarkerInfo,
   markerLib: typeof google.maps.marker,
   map: google.maps.Map,
): google.maps.marker.AdvancedMarkerElement {
   // Create image element
   const markerImgEl = document.createElement('img');
   markerImgEl.alt = markerObj.title || `${markerObj.subcategory} marker`;
   markerImgEl.src = markerObj.selected ? markerObj.icon.selected! : markerObj.icon.active;

   // Build classes
   const sizeClass = SIZE_CLASS_MAP[markerObj.icon.size];
   markerImgEl.className = `${sizeClass} translate-y-1/2 select-none transition-all duration-300 ease-in-out`;

   // Calculate z-index
   let zIndex = 1;
   if (markerObj.subcategory === 'nmedPfasSurfaceWater' || markerObj.subcategory === 'nmedPfasGroundWater') {
      zIndex = 1000;
   }
   if (markerObj.selected) {
      zIndex += 100;
   }

   // Create marker
   return new markerLib.AdvancedMarkerElement({
      map,
      position: { lat: markerObj.coords.lat, lng: markerObj.coords.lng },
      gmpClickable: true,
      content: markerImgEl,
      zIndex,
   });
}

/**
 * Creates an HTML-based marker (for a permit type)
 */
export function createPermitMarker(
   markerObj: MarkerInfo,
   markerLib: typeof google.maps.marker,
   map: google.maps.Map,
): google.maps.marker.AdvancedMarkerElement {
   // Create div element
   const markerHtmlEl = document.createElement('div');
   markerHtmlEl.className = 'rounded-full bg-black text-white text-center py-1 px-3 border border-white text-sm transition-all duration-300 ease-in-out';
   markerHtmlEl.innerHTML = markerObj.markerData?.Bureau || '';

   // Calculate z-index
   const zIndex = markerObj.selected ? 101 : 1;

   // Create marker
   return new markerLib.AdvancedMarkerElement({
      map,
      position: { lat: markerObj.coords.lat, lng: markerObj.coords.lng },
      gmpClickable: true,
      content: markerHtmlEl,
      zIndex,
   });
}

// =================================================================================================
// MARKER STYLING
// =================================================================================================

/**
 * Applies marker style based on state (selected/unselected)
 * Automatically detects marker type (default vs. sourceType) from an element type
 */
export function applyMarkerStyle(
   element: HTMLImageElement | HTMLDivElement,
   markerType: MarkerImageType | null,
   size: MarkerIconSize,
   isSelected: boolean,
): void {
   if (!element || !markerType)
      return;

   // Get the appropriate config
   const configKey = (markerType in MARKER_STYLE_CONFIGS ? markerType : 'DEFAULT') as BaseMarkerType;

   const config = MARKER_STYLE_CONFIGS[configKey];

   // If no config found, return early
   if (!config)
      return;

   const state = isSelected ? config.selected : config.unselected;

   // Apply class changes for all markers
   if (state.removeClasses) {
      element.classList.remove(...state.removeClasses);
   }

   if (configKey === 'DEFAULT' && element instanceof HTMLImageElement) {
      // Update image src
      if (isSelected) {
         element.src = markerIcons[`${markerType}_selected` as MarkerImageType];
      }
      else {
         element.src = markerIcons[markerType];
      }

      // Handle size classes
      const sizeClasses = SIZE_CLASS_MAP[size].split(' ');
      if (isSelected) {
         element.classList.remove(...sizeClasses);
      }
      else {
         element.classList.add(...sizeClasses);
      }
   }

   // Add new classes for all markers
   if (state.classes) {
      element.classList.add(...state.classes);
   }
}
